import { useEffect } from "react";
import { ArrowRight, ArrowUpRight } from "lucide-react";
import { Logo } from "@/components/yanre/Logo";
import { SectionLabel } from "@/components/yanre/SectionLabel";
import { Reveal, RevealLines } from "@/components/yanre/Reveal";
import { Marquee } from "@/components/yanre/Marquee";
import { Founder } from "@/components/yanre/Founder";

const SWATCHES = [
  { name: "signal", hex: "#D10000", cls: "bg-signal", note: "CTA / accents" },
  { name: "signal-hover", hex: "#A80000", cls: "bg-signal-hover", note: "CTA hover (visual est.)" },
  { name: "carbon-950", hex: "#0A0A0B", cls: "bg-carbon-950", note: "deepest bg" },
  { name: "carbon-900", hex: "#0E0E10", cls: "bg-carbon-900", note: "page bg / section-dark" },
  { name: "carbon-800", hex: "#161619", cls: "bg-carbon-800", note: "cards / panels" },
  { name: "carbon-700", hex: "#1F1F23", cls: "bg-carbon-700", note: "borders on dark" },
  { name: "carbon-600", hex: "#2B2B31", cls: "bg-carbon-600", note: "borders / chips" },
  { name: "carbon-500", hex: "#3A3A41", cls: "bg-carbon-500", note: "muted on dark" },
  { name: "titanium", hex: "#C7C8CC", cls: "bg-titanium", note: "primary light text" },
  { name: "steel", hex: "#9A9AA0", cls: "bg-steel", note: "secondary text" },
  { name: "light", hex: "#F4F6FA", cls: "bg-[#F4F6FA]", note: "section-light bg" },
];

const BENEFITS = [
  { num: "01", title: "Factory-Direct Value", body: "A more direct commercial structure designed to reduce unnecessary distribution layers and deliver stronger value to professional buyers." },
  { num: "02", title: "Direct Commercial Support", body: "Talk directly with our specialist team throughout product selection, quotation and project development." },
  { num: "03", title: "Direct After-Sales Support", body: "Professional support connected directly to the manufacturer, helping simplify technical assistance, parts and after-sales communication." },
  { num: "04", title: "Factory-Level Product Expertise", body: "Access to product knowledge and technical information connected to the people and engineering behind the equipment." },
];

const Swatch = ({ s }) => (
  <div className="border border-carbon-700 bg-carbon-800" data-testid={`swatch-${s.name}`}>
    <div className={`h-20 ${s.cls} border-b border-carbon-700`} />
    <div className="p-4">
      <p className="font-mono text-[11px] uppercase tracking-[0.2em] text-white">{s.name}</p>
      <p className="mt-1 font-mono text-[11px] text-signal">{s.hex}</p>
      <p className="mt-2 text-xs text-titanium/50">{s.note}</p>
    </div>
  </div>
);

export default function DesignSystem() {
  useEffect(() => {
    // Internal design reference — never indexable (also disallowed in robots.txt).
    let el = document.head.querySelector('meta[name="robots"]');
    if (!el) {
      el = document.createElement("meta");
      el.setAttribute("name", "robots");
      document.head.appendChild(el);
    }
    el.setAttribute("content", "noindex, nofollow");
  }, []);

  return (
    <div className="bg-carbon-950 font-sans text-titanium" data-testid="design-system-page">
      {/* Header */}
      <header className="sticky top-0 z-50 border-b border-carbon-700 bg-carbon-950/90 backdrop-blur-sm">
        <div className="container-edge flex h-16 items-center justify-between">
          <Logo />
          <span className="hidden font-mono text-[10px] uppercase tracking-[0.3em] text-titanium/50 sm:block">
            Landing Page Engine — Design Foundation v1
          </span>
        </div>
      </header>

      {/* Intro */}
      <section className="section-darker relative overflow-hidden">
        <div className="grid-lines absolute inset-0 opacity-50" />
        <div className="container-edge relative py-24 lg:py-32">
          <Reveal>
            <SectionLabel index="00">Visual Source of Truth</SectionLabel>
          </Reveal>
          <h1 className="mt-8 max-w-5xl font-display text-6xl font-extrabold uppercase leading-[0.86] tracking-tight text-white sm:text-7xl lg:text-8xl">
            <RevealLines
              lines={[
                "Design",
                "Foundation",
                <span key="l3">
                  Locked<span className="text-signal">.</span>
                </span>,
              ]}
            />
          </h1>
          <Reveal delay={0.2}>
            <p className="mt-8 max-w-xl text-lg font-light leading-relaxed text-titanium/80">
              Verified against the YANRE FITNESS EUROPE institutional website. Every landing page —
              Open Your Gym, Personal Training Studio, Residential, Hotel &amp; Resort, Upgrade Your
              Gym, Corporate Fitness — builds on these tokens.
            </p>
          </Reveal>
        </div>
        <Marquee items={["Precision", "Performance", "Confidence", "Engineering", "Since 1997"]} className="border-y border-carbon-700 py-8" />
      </section>

      {/* Colors */}
      <section className="section-dark relative border-t border-carbon-700">
        <div className="container-edge py-24 lg:py-32">
          <Reveal>
            <SectionLabel index="01">Color System</SectionLabel>
          </Reveal>
          <h2 className="mt-8 font-display text-4xl font-extrabold uppercase leading-[0.92] tracking-tight text-white sm:text-5xl lg:text-6xl">
            <RevealLines lines={["Carbon. Titanium.", <span key="s">Signal<span className="text-signal">.</span></span>]} />
          </h2>
          <div className="mt-16 grid grid-cols-2 gap-px sm:grid-cols-3 lg:grid-cols-4" data-testid="color-grid">
            {SWATCHES.map((s) => (
              <Swatch key={s.name} s={s} />
            ))}
          </div>
        </div>
      </section>

      {/* Typography */}
      <section className="section-light relative overflow-hidden">
        <div className="grid-lines-light absolute inset-0 opacity-40" />
        <div className="container-edge relative py-24 lg:py-32">
          <Reveal>
            <SectionLabel index="02" tone="light">Typography</SectionLabel>
          </Reveal>
          <div className="mt-12 space-y-14" data-testid="typography-specimens">
            <Reveal>
              <div>
                <p className="mb-3 font-mono text-[10px] uppercase tracking-[0.25em] text-carbon-900/40">Display H1 — Barlow Condensed 800, uppercase, leading 0.86</p>
                <p className="font-display text-6xl font-extrabold uppercase leading-[0.86] tracking-tight text-carbon-900 sm:text-8xl">Engineered by Experience.</p>
              </div>
            </Reveal>
            <Reveal>
              <div>
                <p className="mb-3 font-mono text-[10px] uppercase tracking-[0.25em] text-carbon-900/40">Display H2 — leading 0.92</p>
                <p className="font-display text-4xl font-extrabold uppercase leading-[0.92] tracking-tight text-carbon-900 sm:text-6xl">Built for Performance.</p>
              </div>
            </Reveal>
            <Reveal>
              <div>
                <p className="mb-3 font-mono text-[10px] uppercase tracking-[0.25em] text-carbon-900/40">Display H3 / card title</p>
                <p className="font-display text-3xl font-extrabold uppercase leading-none text-carbon-900 sm:text-4xl">Metropolitan Performance Club</p>
              </div>
            </Reveal>
            <Reveal>
              <div className="grid gap-10 lg:grid-cols-2">
                <div>
                  <p className="mb-3 font-mono text-[10px] uppercase tracking-[0.25em] text-carbon-900/40">Body — Plus Jakarta Sans 300</p>
                  <p className="max-w-md text-lg font-light leading-relaxed text-carbon-700">
                    YANRE FITNESS EUROPE — the European home of an established international
                    manufacturer of commercial-grade strength systems and complete fitness solutions.
                  </p>
                </div>
                <div>
                  <p className="mb-3 font-mono text-[10px] uppercase tracking-[0.25em] text-carbon-900/40">Mono labels — JetBrains Mono</p>
                  <p className="font-mono text-[11px] uppercase tracking-[0.3em] text-signal">Overline / 0.3em</p>
                  <p className="mt-3 font-mono text-[11px] uppercase tracking-[0.2em] text-carbon-900">Button label / 0.2em</p>
                  <p className="mt-3 font-mono text-xs uppercase tracking-wider text-carbon-900/50">Meta caption / wider</p>
                </div>
              </div>
            </Reveal>
          </div>
        </div>
      </section>

      {/* Buttons + labels */}
      <section className="section-dark border-t border-carbon-700">
        <div className="container-edge py-24 lg:py-32">
          <Reveal>
            <SectionLabel index="03">Buttons &amp; Labels</SectionLabel>
          </Reveal>
          <div className="mt-12 flex flex-col gap-4 sm:flex-row sm:items-center" data-testid="button-specimens">
            <button type="button" className="btn-signal group" data-testid="specimen-btn-signal">
              Talk to a Specialist
              <ArrowRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-1" />
            </button>
            <button type="button" className="btn-ghost border-titanium/30 text-titanium hover:border-signal hover:text-white" data-testid="specimen-btn-ghost">
              Request a Quote
            </button>
            <span className="inline-flex items-center gap-2 font-mono text-[11px] uppercase tracking-[0.2em] text-white" data-testid="specimen-text-link">
              View Case Study
              <ArrowUpRight className="h-4 w-4 text-signal" />
            </span>
          </div>
          <div className="mt-16 grid gap-10 border-t border-carbon-700 pt-12 sm:grid-cols-2" data-testid="label-specimens">
            <Reveal>
              <p className="mb-4 font-mono text-[10px] uppercase tracking-[0.25em] text-titanium/40">Dark sections</p>
              <SectionLabel index="04">European &amp; Global Presence</SectionLabel>
            </Reveal>
            <Reveal>
              <p className="mb-4 font-mono text-[10px] uppercase tracking-[0.25em] text-titanium/40">Chips / tags</p>
              <div className="flex flex-wrap gap-2">
                {["73 Tough Series", "82 Plate-Loaded", "Commercial Cardio", "CE · TÜV · ISO"].map((t) => (
                  <span key={t} className="border border-carbon-600 px-3 py-1.5 font-mono text-[10px] uppercase tracking-wider text-titanium/60">
                    {t}
                  </span>
                ))}
              </div>
            </Reveal>
          </div>
        </div>
      </section>

      {/* Example dark content section — numbered manifesto */}
      <section className="section-darker relative overflow-hidden border-t border-carbon-700">
        <div className="grid-lines absolute inset-0 opacity-50" />
        <div className="container-edge relative py-24 lg:py-32">
          <Reveal>
            <SectionLabel index="04">Numbered Chapters — Example Section</SectionLabel>
          </Reveal>
          <h2 className="mt-8 font-display text-4xl font-extrabold uppercase leading-[0.92] tracking-tight text-white sm:text-5xl lg:text-6xl">
            <RevealLines lines={["More value.", <span key="f" className="text-signal">Fewer layers.</span>]} />
          </h2>
          <div className="mt-16 grid gap-x-16 gap-y-10 sm:grid-cols-2" data-testid="chapter-list">
            {BENEFITS.map((b, i) => (
              <Reveal key={b.num} delay={i * 0.08}>
                <div className="border-t border-carbon-700 pt-8">
                  <div className="grid grid-cols-[auto_1fr] gap-6">
                    <span className="font-display text-4xl font-extrabold text-white/15">{b.num}</span>
                    <div>
                      <h3 className="font-display text-lg font-bold uppercase text-white sm:text-xl">{b.title}</h3>
                      <p className="mt-3 max-w-md text-base leading-relaxed text-titanium/60">{b.body}</p>
                    </div>
                  </div>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* The Man Behind the Machine */}
      <Founder />

      {/* Footer strip */}
      <footer className="border-t border-carbon-700 bg-carbon-950">
        <div className="container-edge flex flex-col items-start justify-between gap-6 py-10 sm:flex-row sm:items-center">
          <Logo />
          <p className="font-mono text-[10px] uppercase tracking-[0.25em] text-titanium/40">
            YANRE FITNESS EUROPE — Landing Page Engine · Design Foundation v1
          </p>
        </div>
      </footer>
    </div>
  );
}
